import type { TriggerRef } from '@rn-primitives/popover';
import {
  CalendarPlusIcon,
  MapIcon,
  MoreHorizontalIcon,
  PencilIcon,
  ShareIcon,
} from 'lucide-react-native';
import * as React from 'react';
import { Pressable } from 'react-native';

import type { EventQuickActionsProps } from './event-quick-actions.types';
import { useEventQuickActions } from './use-event-quick-actions';
import { Button } from '@sixam/ui/button';
import { Icon } from '@sixam/ui/icon';
import { Popover, PopoverContent, PopoverTrigger } from '@sixam/ui/popover';
import { Text } from '@sixam/ui/text';
import type { EventQuickActionId } from '@/lib/event-quick-actions';

const webActions: Record<
  EventQuickActionId,
  Readonly<{ icon: typeof PencilIcon; label: string }>
> = {
  calendar: { icon: CalendarPlusIcon, label: 'Add to calendar' },
  directions: { icon: MapIcon, label: 'Get directions' },
  edit: { icon: PencilIcon, label: 'Edit event' },
  share: { icon: ShareIcon, label: 'Share event' },
};

export function EventQuickActions(props: EventQuickActionsProps) {
  const { actionIds, runAction } = useEventQuickActions(props);
  const popoverTriggerRef = React.useRef<TriggerRef>(null);

  function handleAction(actionId: EventQuickActionId) {
    popoverTriggerRef.current?.close();
    void runAction(actionId);
  }

  return (
    <Popover>
      <PopoverTrigger asChild ref={popoverTriggerRef}>
        <Pressable
          accessibilityHint="Opens event actions"
          accessibilityLabel={`More actions for ${props.event.title}`}
          accessibilityRole="button"
          className="size-11 items-center justify-center rounded-full active:opacity-70"
          testID={`event-quick-actions-${props.event.id}`}
        >
          <Icon as={MoreHorizontalIcon} className="size-5 text-foreground" />
        </Pressable>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-60 gap-1 p-1" side="bottom">
        {actionIds.map((actionId) => {
          const action = webActions[actionId];
          return (
            <Button
              key={actionId}
              className="h-11 justify-start gap-3 px-3"
              onPress={() => handleAction(actionId)}
              variant="ghost"
            >
              <Icon as={action.icon} className="size-4 text-foreground" />
              <Text>{action.label}</Text>
            </Button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

export type { EventQuickActionsProps } from './event-quick-actions.types';
